import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusCircle,
  Edit2,
  Trash2,
  RefreshCw,
  UserPlus,
  UserMinus,
  MessageCircle,
  LogIn,
  LogOut,
  AlertCircle,
  Upload,
  Download,
  FileText,
  Eye,
  Key,
  Activity,
  ChevronRight,
} from 'lucide-react';
import {
  getRecentActivities,
  getActivityFeed,
  getActionColor,
  getActionBgColor,
  type ActivityFeedItem,
  type ActivityFeedGroup,
} from '@/services/activityFeedService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';

// Icon mapping
const ActionIcons: Record<string, React.ElementType> = {
  'Create': PlusCircle,
  'Update': Edit2,
  'Delete': Trash2,
  'StatusChange': RefreshCw,
  'Assign': UserPlus,
  'Unassign': UserMinus,
  'Comment': MessageCircle,
  'Login': LogIn,
  'Logout': LogOut,
  'LoginFailed': AlertCircle,
  'Upload': Upload,
  'Download': Download,
  'Export': FileText,
  'View': Eye,
  'PasswordChanged': Key,
};

interface ActivityFeedProps {
  limit?: number;
  showHeader?: boolean;
  showViewAll?: boolean;
  userId?: string;
  entityType?: string;
  entityId?: string;
  grouped?: boolean;
  maxHeight?: string;
}

export function ActivityFeed({
  limit = 10,
  showHeader = true,
  showViewAll = true,
  userId,
  entityType,
  entityId,
  grouped = false,
  maxHeight = '400px',
}: ActivityFeedProps) {
  const [items, setItems] = useState<ActivityFeedItem[]>([]);
  const [groups, setGroups] = useState<ActivityFeedGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      setError(null);
      try {
        if (grouped) {
          const response = await getActivityFeed({
            userId,
            entityId,
            entityTypes: entityType ? [entityType] : undefined,
            limit,
            groupByDate: true,
          });
          setGroups(response.groups);
          setItems(response.items);
        } else {
          const data = await getRecentActivities(limit);
          setItems(data);
        }
      } catch {
        setError('Aktiviteler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [limit, userId, entityType, entityId, grouped]);

  const renderActivityItem = (item: ActivityFeedItem) => {
    const IconComponent = ActionIcons[item.action] || Activity;
    const colorClass = getActionColor(item.action);
    const bgClass = getActionBgColor(item.action);

    return (
      <div key={item.id} className="flex gap-3 py-3">
        {/* User Avatar or Action Icon */}
        <div className="flex-shrink-0">
          {item.userName ? (
            <Avatar className="size-8">
              <AvatarImage src={item.userAvatarUrl || undefined} />
              <AvatarFallback className="text-xs bg-slate-100 text-slate-600">
                {item.userInitials || '?'}
              </AvatarFallback>
            </Avatar>
          ) : (
            <div className={`size-8 rounded-full flex items-center justify-center ${bgClass}`}>
              <IconComponent className={`size-4 ${colorClass}`} />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-700">
            {item.description}
          </p>
          {item.entityUrl && item.entityName && (
            <Link
              to={item.entityUrl}
              className="text-sm text-[color:var(--dg-brand)] hover:text-[color:var(--dg-brand-deep)] hover:underline"
            >
              {item.entityName}
            </Link>
          )}
          {item.details && (
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              {item.details}
            </p>
          )}
          <p className="text-xs text-slate-400 mt-1">
            {item.relativeTime}
          </p>
        </div>

        {/* Action Icon (small, on the right) */}
        <div className={`flex-shrink-0 p-1.5 rounded ${bgClass}`}>
          <IconComponent className={`size-3.5 ${colorClass}`} />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        {showHeader && (
          <CardHeader className="pb-3">
            <Skeleton className="h-5 w-32" />
          </CardHeader>
        )}
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="size-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        {showHeader && (
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Son Aktiviteler</CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-slate-400">
            <AlertCircle className="size-8 mb-2" />
            <p className="text-sm">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {showHeader && (
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Activity className="size-4 text-slate-500" />
            Son Aktiviteler
          </CardTitle>
          {showViewAll && (
            <Link to="/audit-logs">
              <Button variant="ghost" size="sm" className="text-xs gap-1">
                Tümü
                <ChevronRight className="size-3" />
              </Button>
            </Link>
          )}
        </CardHeader>
      )}
      <CardContent>
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-slate-400">
            <Activity className="size-8 mb-2" />
            <p className="text-sm">Henüz aktivite yok</p>
          </div>
        ) : grouped && groups.length > 0 ? (
          <ScrollArea style={{ maxHeight }}>
            <div className="space-y-4">
              {groups.map((group) => (
                <div key={group.groupTitle}>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                    {group.groupTitle}
                  </h4>
                  <div className="divide-y divide-slate-100">
                    {group.items.map(renderActivityItem)}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <ScrollArea style={{ maxHeight }}>
            <div className="divide-y divide-slate-100">
              {items.map(renderActivityItem)}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}

// Compact version for sidebars or small widgets
export function ActivityFeedCompact({
  limit = 5,
}: {
  limit?: number;
}) {
  const [items, setItems] = useState<ActivityFeedItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const data = await getRecentActivities(limit);
        setItems(data);
      } catch {
        // intentionally empty
      } finally {
        setLoading(false);
      }
    };
    fetchActivities();
  }, [limit]);

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item) => {
        const IconComponent = ActionIcons[item.action] || Activity;
        const colorClass = getActionColor(item.action);

        return (
          <div
            key={item.id}
            className="flex items-center gap-2 text-sm py-1.5"
          >
            <IconComponent className={`size-3.5 flex-shrink-0 ${colorClass}`} />
            <span className="truncate text-slate-600">{item.description}</span>
            <span className="text-xs text-slate-400 flex-shrink-0 ml-auto">
              {item.relativeTime}
            </span>
          </div>
        );
      })}
    </div>
  );
}
